/**
 * Custom Hook: useExerciseModal
 *
 * Responsabilidade:
 * - Gerenciar todo o estado do Modal de Exercícios (formulário, previews, loading).
 * - Orquestrar efeitos colaterais (useEffect).
 * - Expor métodos para manipulação da UI e submissão de dados.
 * - Integrar lógica de negócios e utilitários.
 */

import { useState, useEffect, FormEvent, ChangeEvent } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import Strings from "../../constants/Strings";
import utils from "../../helpers/utils";
import {
  ExerciseModalLogicProps,
  ExerciseFormState,
} from "./exerciseModalTypes";
import { saveExercise } from "./exerciseModalLogic";
import {
  validateImage,
  validateVideo,
  readFileAsBase64,
} from "./exerciseModalUtils";

export const useExerciseModal = ({
  isOpen,
  onBack,
  data,
  onSuccess = () => {},
}: ExerciseModalLogicProps) => {
  const { getUser } = useAuth();
  const user = getUser();
  const professional_id = user?.id;

  const [form, setForm] = useState<ExerciseFormState>({
    name: "",
    image: "",
    professional_id: professional_id ? Number(professional_id) : undefined,
    video: "",
    description: "",
    id: undefined,
  });

  const [imagePreview, setImagePreview] = useState<string>("");
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [videoSectionExpanded, setVideoSectionExpanded] = useState(true);
  const [imageSectionExpanded, setImageSectionExpanded] = useState(true);

  const isReadOnly =
    form.id !== undefined &&
    form.id !== null &&
    (form.all_view === true || data?.all_view === true) &&
    !user?.is_admin;

  useEffect(() => {
    if (isOpen && data) {
      setForm((prevForm) => ({
        ...prevForm,
        id: undefined,
        professional_id: professional_id ? Number(professional_id) : undefined,
        ...data,
      }));
      setImagePreview(
        data.image_url ||
          utils.getUriImage(data.image ?? Strings.default_photo, "all_view"),
      );

      if (data.video?.startsWith("data:")) {
        setVideoPreview(data.video);
      } else if (data.video) {
        setVideoPreview(
          utils.getUriImage(
            data.video,
            data.professional_id ? String(data.professional_id) : "all_view",
          ),
        );
      } else {
        setVideoPreview("");
      }

      setVideoSectionExpanded(true);
      setImageSectionExpanded(true);
    }
  }, [isOpen, data, professional_id]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isReadOnly) {
      toast.error("Este exercício é público e não pode ser alterado.");
      return;
    }

    setLoading(true);

    const result = await saveExercise(form, professional_id);

    setLoading(false);

    if (result.ok) {
      toast.success(
        form.id !== undefined && form.id !== null && form.id !== 0
          ? "Exercício atualizado com sucesso!"
          : "Exercício criado com sucesso!",
      );
      onSuccess();
      onBack();
    } else {
      toast.error(result.error ?? "Erro ao salvar o exercício.");
    }
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateImage(file)) return;

      try {
        const base64String = await readFileAsBase64(file);
        setForm({ ...form, image: base64String });
        setImagePreview(base64String);
        setImageSectionExpanded(true);
      } catch (error) {
        console.error("Error reading image file", error);
        toast.error("Erro ao processar a imagem.");
      }
    }
  };

  const handleVideoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateVideo(file)) return;

      try {
        const base64String = await readFileAsBase64(file);
        setForm({ ...form, video: base64String });
        setVideoPreview(base64String);
        setVideoSectionExpanded(true);
      } catch (error) {
        console.error("Error reading video file", error);
        toast.error("Erro ao processar o vídeo.");
      }
    }
  };

  const clearImage = () => {
    setForm({ ...form, image: "" });
    setImagePreview("");
  };

  const toggleVideoSection = () => setVideoSectionExpanded((prev) => !prev);
  const toggleImageSection = () => setImageSectionExpanded((prev) => !prev);

  const updateField = <K extends keyof ExerciseFormState>(
    field: K,
    value: ExerciseFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return {
    form,
    imagePreview,
    videoPreview,
    loading,
    videoSectionExpanded,
    imageSectionExpanded,
    isReadOnly,
    submit: handleSubmit,
    handleImageChange,
    handleVideoChange,
    clearImage,
    toggleVideoSection,
    toggleImageSection,
    updateField,
  };
};
